// @flow

import { isMobileBrowser } from '../../../../../react/features/base/environment/utils';
import { IconControlSharing } from '../../../../../react/features/base/icons/svg';
import { getParticipantByIdOrUndefined } from '../../../../../react/features/base/participants/functions';
import ContextMenuItem from '../../../../../react/features/base/ui/components/web/ContextMenuItem';
import { showWarningNotification } from '../../../../../react/features/notifications/actions';
import { NOTIFICATION_TIMEOUT_TYPE } from '../../../../../react/features/notifications/constants';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { STATUSES } from '../../../../components/wildix-remote-control/constants';
import {
    isFirefoxLocalSharing, isScreenSharingPlayingByUserID,
    isSharingTab,
    isVersionSupportControl
} from '../../../../components/wildix-remote-control/functions';
import Env from '../../../../modules/Environment';

import {
    createRequest,
    sendEventServiceNotInstalled,
    sendEventToCloseConnection,
    stopController
} from '../../actions';

const ControlSharingMenuButton = ownProps => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { enableScreenSharing } = useSelector(state => state['features/base/config']);
    const {
        active,
        launchedControlID,
        controlledUserId,
        statusControl
    } = useSelector(state => state['wildix/features/control-sharing']);
    const { isConnected } = useSelector(state => state['wildix/features/wiservice']);
    const { participantID } = ownProps;
    const isSharingActiveWithUserID = participantID === controlledUserId || participantID === launchedControlID;

    // Only the local participant won't have id for the time when the conference is not yet joined.
    const { supportsWildixRemoteControl } = useSelector(state => getParticipantByIdOrUndefined(state, participantID));

    const localScreenSharingPlaying = useSelector(isScreenSharingPlayingByUserID);
    const isLocalSharingFirefox = useSelector(isFirefoxLocalSharing);
    const isLocalSharingTab = useSelector(isSharingTab);
    const isSupportControl = useSelector(isVersionSupportControl);

    const isStatusOpened = statusControl === STATUSES.OPENED;
    const isStatusRequesting = statusControl === STATUSES.REQUESTING;
    const isButtonBusy = !active && isSharingActiveWithUserID && isStatusOpened;
    const isUserSupportSharing = !supportsWildixRemoteControl;
    const isWiServiceSupport = isConnected && isSupportControl;
    const isLocalScreenSharing = enableScreenSharing
        && localScreenSharingPlaying
        && (!active || isSharingActiveWithUserID);
    const isHideButton = Env.isWizyconf() || isMobileBrowser() || isLocalSharingFirefox;

    const getTranslate = useMemo(() => {
        let result = '';

        if (isButtonBusy) {
            result = t('controlSharing.btnRemoteControlBusy');
        } else if (isUserSupportSharing) {
            result = t('controlSharing.btnUserDoesNotSupportControl');
        } else if (active && isSharingActiveWithUserID) {
            result = t('controlSharing.btnRemoteControlDisable');
        } else if (isStatusRequesting) {
            result = t('controlSharing.btnRemoteControlRequesting');
        } else if (isLocalScreenSharing) {
            result = t('controlSharing.btnGiveRemoteControl');
        } else {
            result = t('controlSharing.btnRemoteControlEnable');
        }

        return result;
    }, [ isButtonBusy, isUserSupportSharing, active, isLocalScreenSharing ]);

    const isButtonDisabled = isButtonBusy || isUserSupportSharing
        || isStatusRequesting || (active && !isSharingActiveWithUserID);
    const getClassDisabled = isButtonDisabled ? 'sharing-disabled' : '';

    /**
     * Click handler for the element.
     *
     * @returns {void}
     */

    const onClick = useCallback(
        () => {
            if (!isWiServiceSupport && isLocalScreenSharing) {
                dispatch(sendEventServiceNotInstalled(participantID, isLocalScreenSharing));
            } else if (isLocalSharingTab) {
                dispatch(showWarningNotification({
                    titleKey: 'controlSharing.titleDialogControl',
                    descriptionKey: 'controlSharing.screenSharingTabLocal'
                }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
            } else if (active) {
                if (isLocalScreenSharing) {
                    dispatch(sendEventToCloseConnection());
                } else {
                    dispatch(stopController());
                }
            } else {
                dispatch(createRequest(participantID, isLocalScreenSharing));
            }
        }, [ isWiServiceSupport, isLocalScreenSharing, participantID, active ]
    );

    return (
        <>
            { !isHideButton && <ContextMenuItem
                accessibilityLabel = { t('controlSharing.titleDialogControl') }
                className = { getClassDisabled }
                text = { getTranslate }
                icon = { IconControlSharing }
                disabled = { isButtonDisabled }
                id = { `control_${participantID}` }
                onClick = { onClick }
            /> }
        </>
    );
};

export default ControlSharingMenuButton;
